// valet-keychain-qr — qr-label-pdf: live preview of the label layout,
// reflecting cols x rows / label size BEFORE any PDF is generated. Cheap
// DOM/CSS grid — no @react-pdf/renderer import here, so it updates instantly
// on every config keystroke without re-triggering the heavy PDF chunk. The QR
// itself is drawn as inline SVG from the same `qrcode` encoder the PDF uses
// (sync `create`, no canvas/PNG), and only for the previewed (first) page.

import { useTranslation } from '@jq4race/i18n';
import QRCode from 'qrcode';
import { memo, useMemo } from 'react';

import {
  type LabelGridConfig,
  paginateLabels,
  QR_ERROR_CORRECTION_LEVEL,
  qrPayloadForCode,
} from '../pdf/label-grid';

export interface LabelGridPreviewProps {
  config: LabelGridConfig;
  codes: string[];
}

// Quiet zone (modules) around the symbol — mirrors the `margin: 1` the PDF
// export passes to QRCode.toDataURL, so the preview matches the printed label.
const QUIET_ZONE = 1;

function QrCodeSvgBase({ code, label }: { code: string; label: string }) {
  const { path, viewBox } = useMemo(() => {
    const { modules } = QRCode.create(qrPayloadForCode(code), {
      errorCorrectionLevel: QR_ERROR_CORRECTION_LEVEL,
    });
    const size = modules.size;
    let d = '';
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (modules.get(row, col)) d += `M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`;
      }
    }
    const total = size + QUIET_ZONE * 2;
    return { path: d, viewBox: `0 0 ${total} ${total}` };
  }, [code]);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={viewBox}
      shapeRendering="crispEdges"
      className="aspect-square h-auto w-full max-w-10 rounded-sm bg-white"
    >
      <path d={path} fill="#000" />
    </svg>
  );
}

const QrCodeSvg = memo(QrCodeSvgBase);

export function LabelGridPreview({ config, codes }: LabelGridPreviewProps) {
  const { t } = useTranslation('qr-generator');
  const pages = useMemo(
    () => paginateLabels(codes, config.columns, config.rows),
    [codes, config.columns, config.rows],
  );
  const firstPage = pages[0] ?? [];

  return (
    <div className="space-y-3">
      <h3 className="text-[14px] font-medium text-jq-charcoal">{t('preview.heading')}</h3>
      <p className="text-[12px] text-jq-graphite">
        {t('preview.pageCount', { count: pages.length })} ·{' '}
        {t('preview.labelCount', { count: codes.length })}
      </p>
      <div
        data-testid="label-grid-preview"
        className="grid gap-1.5 rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-3"
        style={{ gridTemplateColumns: `repeat(${Math.max(1, config.columns)}, minmax(0, 1fr))` }}
      >
        {firstPage.map((code) => (
          <div
            key={code}
            className="flex flex-col items-center justify-center gap-1 rounded border border-jq-sand/60 p-1.5 text-[8px] text-jq-graphite"
          >
            <QrCodeSvg code={code} label={t('preview.qrAlt', { code })} />
            <span className="jq-mono truncate">{code}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
